function normalizeGender(student) {
  const g = String(student?.genderLabel || student?.gender || "")
    .trim()
    .toLowerCase();
  if (!g) return "other";
  if (g.startsWith("f") || g.includes("female") || g.includes("girl")) {
    return "female";
  }
  if (g.startsWith("m") || g.includes("male") || g.includes("boy")) {
    return "male";
  }
  return "other";
}

function classStudentList(cls) {
  if (!cls) return [];
  if (Array.isArray(cls.students) && cls.students.length) {
    return cls.students;
  }
  const buckets = [
    ...(cls.presentStudents || []),
    ...(cls.registeredStudents || []),
  ];
  const seen = new Set();
  const list = [];
  buckets.forEach((student) => {
    const key = student.id || `${student.grNo}-${student.name}`;
    if (seen.has(key)) return;
    seen.add(key);
    list.push(student);
  });
  return list;
}

export function classGenderRow(cls) {
  const students = classStudentList(cls);
  let male = 0;
  let female = 0;
  let other = 0;
  students.forEach((s) => {
    const bucket = normalizeGender(s);
    if (bucket === "female") female += 1;
    else if (bucket === "male") male += 1;
    else other += 1;
  });
  const total =
    students.length ||
    Number(cls.presentCount ?? cls.totalCount) ||
    male + female + other;
  return {
    className: cls.className || cls.name || "—",
    classID: cls.classID ?? cls.id,
    male,
    female,
    other,
    total,
  };
}

export function flattenClassStrengthPayload(data, campusID) {
  if (!data) return [];
  let classes = [];
  if (Array.isArray(data.classes)) classes = data.classes;
  else if (Array.isArray(data.campuses)) {
    data.campuses.forEach((campus) => {
      if (
        campusID > 1 &&
        campus.campusID != null &&
        Number(campus.campusID) !== Number(campusID)
      ) {
        return;
      }
      if (Array.isArray(campus.classes)) {
        classes = classes.concat(campus.classes);
      }
    });
  } else if (Array.isArray(data)) {
    classes = data;
  }

  return classes
    .map(classGenderRow)
    .filter((r) => r.total > 0)
    .sort((a, b) => b.total - a.total);
}

export function groupPendingByName(rows, nameKey, idKey) {
  const map = new Map();
  rows.forEach((row) => {
    const name = String(row[nameKey] || "—").trim() || "—";
    const key = `${name}-${row[idKey] || ""}`;
    const existing = map.get(key) || {
      name,
      count: 0,
      localID: row[idKey] || row.localID || row.tID || row.sID || 0,
      tID: row.tID,
      studentId: row.sID || row.studentID || row.st?.id,
      grNo: row.grNo || row.st?.grNo,
      className: row.className || row.meta_class || row.st?.className,
      contact:
        row.contact_student ||
        row.contact_father ||
        row.contact ||
        row.phoneNumber,
      phoneNumber: row.phoneNumber || row.contact,
    };
    existing.count += 1;
    map.set(key, existing);
  });
  return [...map.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

/** Safe date formatting shared by the request lists. */
export function formatRequestDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function daysBetween(from, to) {
  if (!from || !to) return 0;
  const a = new Date(from);
  const b = new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  const diff = Math.round((b.getTime() - a.getTime()) / 86400000) + 1;
  return diff > 0 ? diff : 0;
}

function requestStatus(row) {
  const raw = String(row.status || "").trim();
  if (!raw) return "Pending";
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

/**
 * Pending staff leave — grouped by name; count only on dashboard (no reason/dates).
 */
export function summarizeLeavePending(items) {
  const pending = (items || []).filter((row) =>
    String(row.status || "")
      .toLowerCase()
      .includes("pending")
  );
  return groupPendingByName(pending, "name", "tID");
}

/**
 * Pending student leaving requests — grouped by student name.
 */
export function summarizeStudentRequests(items) {
  const pending = (items || []).filter((row) =>
    String(row.status || "")
      .toLowerCase()
      .includes("pending")
  );
  const normalized = pending.map((row) => {
    const st = row.st || row.student || {};
    return {
      ...row,
      displayName:
        st.name ||
        row.name ||
        row.student_name ||
        `${st.firstName || ""} ${st.lastName || ""}`.trim() ||
        "—",
      sID: st.id || st.sID || row.sID,
      grNo: st.grNo || row.grNo,
      className: st.className || row.meta_class,
      contact: row.contact_father || st.contact_student || st.contact,
    };
  });
  return groupPendingByName(normalized, "displayName", "sID");
}

export function topSubjectRows(subData, limit = 6) {
  if (!Array.isArray(subData)) return [];
  return [...subData]
    .map((row) => ({
      subject: row.subject || row.name || "—",
      students: Number(row.totalStudents ?? row.students) || 0,
    }))
    .sort((a, b) => b.students - a.students)
    .slice(0, limit);
}

export async function loadClassStrengthSummary({ get, domain, token, db, campusID }) {
  const endDate = new Date().toISOString().split("T")[0];
  const url =
    domain +
    "Students/ClassWiseStudentStatusReport?db=" +
    encodeURIComponent(db) +
    "&cID=" +
    campusID +
    "&endDate=" +
    endDate +
    "&classID=0&sectionID=0&departmentID=0";

  const response = await get({
    url,
    token,
    dashboard: true,
  });

  let data = response;
  if (response && response.status === "success" && response.data != null) {
    data = response.data;
  } else if (response?.data && typeof response.data === "object") {
    data = response.data;
  }
  const rows = flattenClassStrengthPayload(data, campusID);
  const totals = rows.reduce(
    (acc, r) => {
      acc.male += r.male;
      acc.female += r.female;
      acc.other += r.other;
      acc.total += r.total;
      return acc;
    },
    { male: 0, female: 0, other: 0, total: 0 }
  );
  return { rows, totals };
}

export async function loadPendingLeaveRequests({ get, domain, token, db, campusID, userID, reportDomain }) {
  const loadAll = String(reportDomain || "").includes("myskooliuss")
    ? campusID === 1
    : true;
  const dt = new Date().toISOString().split("T")[0];
  const url =
    domain +
    "LeaveRequests/GetByPagination?db=" +
    encodeURIComponent(db) +
    "&cID=" +
    campusID +
    "&userID=" +
    (userID || 1) +
    "&loadAll=" +
    loadAll +
    "&dt=" +
    dt +
    "&pageNumber=1&pageSize=200&sortBy=date&sortOrder=desc";

  const res = await get({ url, token, dashboard: true });
  let items = [];
  if (res?.data?.data) items = res.data.data;
  else if (Array.isArray(res?.data)) items = res.data;
  else if (Array.isArray(res)) items = res;
  return summarizeLeavePending(items);
}

export async function loadPendingStudentRequests({ get, domain, token, db, campusID }) {
  const params = new URLSearchParams({
    db,
    cID: String(campusID),
    status: "pending",
    type: "",
    requestedBy: "",
    page: "1",
    pageSize: "200",
  });
  const url = domain + "studentLeftRequest/LoadData?" + params.toString();
  const res = await get({ url, token, dashboard: true });

  let items = [];
  if (res?.data?.data) items = res.data.data;
  else if (Array.isArray(res?.data)) items = res.data;
  else if (Array.isArray(res)) items = res;

  return summarizeStudentRequests(items);
}
